import { useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { AssociateMobileNav, Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { QrCode, Wallet, Search, Gift, Heart, User, ChevronRight, Star, Check } from "lucide-react";
import { ProCard } from "@/pages/Home";

function Shell({ children, title }) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50 pb-20 md:pb-0">
      <Header/>
      <main className="flex-1">
        {title && <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6"><h1 className="text-2xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>{title}</h1></div>}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">{children}</div>
      </main>
      <AssociateMobileNav/>
    </div>
  );
}

export function AssocHome() {
  const { user } = useAuth();
  const [pros, setPros] = useState([]);
  const [benefits, setBenefits] = useState([]);
  useEffect(()=>{
    api.get("/professionals",{params:{featured:true,limit:6}}).then(r=>setPros(r.data));
    api.get("/benefits",{params:{limit:4}}).then(r=>setBenefits(r.data));
  }, []);
  return (
    <Shell>
      <div className="rounded-3xl gs-hero-bg text-white p-6 gs-shadow-lg">
        <div className="text-sm text-white/80">Olá,</div>
        <div className="text-2xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>{user?.name?.split(" ")[0] || "Associado"} 👋</div>
        <p className="mt-2 text-white/85 text-sm">O que você procura hoje?</p>
        <Link to="/app/associado/buscar" data-testid="assoc-home-search" className="mt-4 flex items-center gap-2 bg-white text-slate-500 rounded-xl px-4 py-3 text-sm">
          <Search className="w-4 h-4"/>Buscar profissionais, clínicas, serviços…
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-6">
        <Link to="/app/associado/carteira" data-testid="assoc-tile-carteira" className="rounded-2xl bg-[#0F172A] text-white p-5 gs-shadow"><Wallet className="w-5 h-5 mb-2"/>Carteira Digital<div className="text-xs text-white/70">Acessar QR Code</div></Link>
        <Link to="/app/associado/beneficios" data-testid="assoc-tile-beneficios" className="rounded-2xl bg-[#FF7D54] text-white p-5 gs-shadow"><Gift className="w-5 h-5 mb-2"/>Benefícios<div className="text-xs text-white/85">Ver catálogo</div></Link>
        <Link to="/app/associado/pedidos" data-testid="assoc-tile-pedidos" className="rounded-2xl bg-white border p-5"><Star className="w-5 h-5 mb-2 text-[#0D7C66]"/>Meus Pedidos<div className="text-xs text-slate-500">Acompanhar leads</div></Link>
        <Link to="/app/associado/favoritos" data-testid="assoc-tile-favoritos" className="rounded-2xl bg-white border p-5"><Heart className="w-5 h-5 mb-2 text-[#FF7D54]"/>Favoritos<div className="text-xs text-slate-500">Salvos por você</div></Link>
      </div>

      <h2 className="text-lg font-bold mt-8 mb-3">Profissionais em destaque</h2>
      <div className="grid sm:grid-cols-2 gap-4">{pros.map(p=><ProCard key={p.id} p={p}/>)}</div>

      <h2 className="text-lg font-bold mt-8 mb-3">Benefícios do mês</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {benefits.map(b=>(
          <Card key={b.id} className="p-4 border-slate-200 gs-shadow"><Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">{b.category}</Badge><div className="font-semibold mt-2">{b.title}</div><div className="text-xs text-slate-500 mt-1">{b.partner_name} • {b.city}</div></Card>
        ))}
      </div>
    </Shell>
  );
}

export function AssocBuscar() {
  const nav = useNavigate();
  const [q,setQ] = useState("");
  return (
    <Shell title="Buscar">
      <div className="flex gap-2"><Input data-testid="assoc-search-q" value={q} onChange={e=>setQ(e.target.value)} placeholder="Especialidade, profissional, serviço…"/><Button data-testid="assoc-search-go" onClick={()=>nav(`/buscar?q=${encodeURIComponent(q)}`)} className="bg-[#0D7C66] hover:bg-[#074E41]">Buscar</Button></div>
      <p className="text-xs text-slate-500 mt-3">A busca abrirá o portal com todos os filtros disponíveis.</p>
    </Shell>
  );
}

export function AssocBeneficios() {
  const [items, setItems] = useState([]);
  useEffect(()=>{ api.get("/benefits").then(r=>setItems(r.data)); }, []);
  return (
    <Shell title="Benefícios">
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map(b=>(
          <Card key={b.id} className="overflow-hidden border-slate-200 gs-shadow" data-testid={`assoc-benef-${b.id}`}>
            {b.image_url && <img src={b.image_url} alt={b.title} className="h-32 w-full object-cover"/>}
            <div className="p-4">
              <Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">{b.category}</Badge>
              <div className="font-semibold mt-2">{b.title}</div>
              <div className="text-xs text-slate-500 mt-1">{b.partner_name} • {b.city}</div>
              <Button data-testid={`assoc-benef-use-${b.id}`} size="sm" className="mt-3 w-full bg-[#FF7D54] hover:bg-[#e56a44]">Utilizar benefício</Button>
            </div>
          </Card>
        ))}
      </div>
    </Shell>
  );
}

export function AssocCarteira() {
  const { user, ready } = useAuth();
  const [w, setW] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(()=>{
    if (!ready) return;
    if (!user) { setErr("Você precisa entrar como associado para ver sua carteira."); return; }
    if (user.role !== "associate") { setErr("A carteira digital está disponível apenas para associados."); return; }
    api.get("/wallet")
      .then(r=>setW(r.data))
      .catch(e=>setErr(e.response?.data?.detail || "Erro ao carregar carteira"));
  }, [ready, user]);

  if (err) return <Shell title="Carteira Digital"><Card className="p-6 gs-shadow text-slate-700" data-testid="wallet-error">{err}</Card></Shell>;
  if (!ready || (!w && !err)) return <Shell title="Carteira Digital"><Card className="p-6 gs-shadow">Carregando…</Card></Shell>;
  return (
    <Shell title="Carteira Digital">
      <div className="relative rounded-3xl bg-[#0F172A] text-white p-6 gs-shadow-lg overflow-hidden max-w-md">
        <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#0D7C66]/40 blur-2xl"/>
        <div className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-[#FF7D54]/30 blur-2xl"/>
        <div className="relative">
          <div className="flex items-center justify-between">
            <div className="font-extrabold text-lg" style={{fontFamily:"Plus Jakarta Sans"}}>GIRO<span className="text-[#FF7D54]">+</span>SAÚDE</div>
            <Badge className="bg-white/10 border-0 text-white">Associado</Badge>
          </div>
          <div className="mt-6 text-xs uppercase tracking-widest text-white/60">Nome</div>
          <div className="font-semibold text-lg" data-testid="wallet-name">{w.name}</div>
          <div className="mt-3 text-xs uppercase tracking-widest text-white/60">Nº associado</div>
          <div className="font-mono tracking-wider" data-testid="wallet-number">{w.number}</div>
          <div className="mt-4 flex justify-between text-xs text-white/70"><div>Plano<div className="text-white font-semibold" data-testid="wallet-plan">{w.plan}</div></div><div>Validade<div className="text-white font-semibold" data-testid="wallet-validity">{w.validity}</div></div><div>Status<div className="text-[#7CE0C6] font-semibold uppercase" data-testid="wallet-status">{w.status}</div></div></div>
          <div className="mt-5 bg-white p-3 rounded-xl w-32 h-32 mx-auto flex items-center justify-center" data-testid="wallet-qr-code">
            <div className="qr-pattern w-full h-full rounded-lg"/>
          </div>
          <div className="text-center text-xs text-white/60 mt-2">Apresente para validar benefícios</div>
        </div>
      </div>
    </Shell>
  );
}

export function AssocPedidos() {
  const [items, setItems] = useState([]);
  useEffect(()=>{ api.get("/leads").then(r=>setItems(r.data)); }, []);
  return (
    <Shell title="Meus Pedidos">
      {items.length===0 && <div className="text-slate-500">Você ainda não fez solicitações. Encontre um profissional na busca e clique em "Tenho Interesse".</div>}
      <div className="space-y-3">
        {items.map(l=>(
          <div key={l.id} className="rounded-2xl bg-white border p-4 flex items-center justify-between gs-shadow" data-testid={`pedido-${l.number}`}>
            <div>
              <div className="text-xs text-slate-500">#{l.number}</div>
              <div className="font-semibold">{l.service || "Solicitação de contato"}</div>
              <div className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString("pt-BR")}</div>
            </div>
            <Badge className="bg-[#E6F4F1] text-[#0D7C66] border-0">{l.status}</Badge>
          </div>
        ))}
      </div>
    </Shell>
  );
}

export function AssocPerfil() {
  const { user, logout } = useAuth();
  return (
    <Shell title="Perfil">
      <div className="rounded-2xl bg-white border p-5 gs-shadow">
        <div className="text-sm text-slate-500">Nome</div><div className="font-semibold">{user?.name}</div>
        <div className="mt-3 text-sm text-slate-500">E-mail</div><div className="font-semibold">{user?.email}</div>
        <div className="mt-3 text-sm text-slate-500">Plano</div><div className="font-semibold">{user?.plan||"—"}</div>
        <Button data-testid="perfil-logout" onClick={logout} variant="outline" className="mt-5">Sair</Button>
      </div>
    </Shell>
  );
}
